import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';
import type { FlatGraphNode } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { QueryFilter } from '@/types';

import { NodeAddRelationship } from './NodeCard/NodeAddRelationship';
import { NodeFilterGroup } from './NodeCard/NodeFilterGroup';
import { NodeLabel } from './NodeCard/NodeLabel';
import { normalizeFilters } from './utils/normalizeFilter';

interface NodeCardProps {
  node: FlatGraphNode;
  isExpanded: boolean;
  onToggle: (nodeId: string) => void;
  editable?: boolean;
  onAddRelationship?: (nodeId: string) => void;
  onUpdateFilters?: (nodeId: string, filters: QueryFilter[]) => void;
  onUpdateLabel?: (nodeId: string, newLabel: string) => void;
  onDeleteNode?: (nodeId: string) => void;
  isDeletable?: boolean;
}

/**
 * NodeCard - Collapsible card displaying a query node
 */
export const NodeCard = ({
  node,
  isExpanded,
  onToggle,
  editable = false,
  onAddRelationship,
  onUpdateFilters,
  onUpdateLabel,
  onDeleteNode,
  isDeletable = false,
}: NodeCardProps) => {
  const labels = node.data.labels || [];
  const firstLabel = labels[0] || 'Unknown';
  const filterCount = (node.data.filters || []).length;

  const [entityFilterDefinitions, setEntityFilterDefinitions] = useState<
    SimpleFilterDefinition[] | null
  >([]);

  const [isLoadingFields, setIsLoadingFields] = useState(false);

  // Normalize filters to explicit AND format
  // Converts implicit AND (flat array) to explicit AND (wrapped in logical operator)
  // All downstream logic can assume filters are in explicit format
  const normalizedFilters = useMemo(() => {
    return normalizeFilters(node.data.filters || []);
  }, [node.data.filters]);

  const filters = normalizedFilters;

  useEffect(() => {
    const loadFieldDefinitions = async () => {
      const entity = getQueryEntity(firstLabel);
      if (!entity?.getSimpleFilters) {
        setEntityFilterDefinitions(null);
        return;
      }

      setIsLoadingFields(true);
      try {
        const definitions = await entity.getSimpleFilters();
        setEntityFilterDefinitions(definitions);
      } catch (error) {
        console.error('Failed to load field definitions:', error);
        setEntityFilterDefinitions(null);
      } finally {
        setIsLoadingFields(false);
      }
    };

    loadFieldDefinitions();
  }, [firstLabel]); // Re-fetch when label changes

  // Wrapper for label changes to match NodeLabel's expected signature
  const handleLabelChange = (newLabel: string) => {
    onUpdateLabel?.(node.id, newLabel);
  };

  return (
    <div className="border-chariot-stroke bg-chariot-background-primary rounded-lg border">
      <NodeLabel
        node={node}
        isExpanded={isExpanded}
        editable={editable}
        firstLabel={firstLabel}
        filterCount={filterCount}
        onToggle={onToggle}
        onUpdateLabel={onUpdateLabel}
        handleLabelChange={handleLabelChange}
        onDeleteNode={onDeleteNode}
        isDeletable={isDeletable}
      />

      {/* Expandable content with CSS Grid collapse */}
      <div
        id={`node-content-${node.id}`}
        role="region"
        aria-label={`${firstLabel} node details`}
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] opacity-100'
            : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <NodeFilterGroup
            node={node}
            filters={filters}
            editable={editable}
            isLoadingFields={isLoadingFields}
            filterDefinitions={entityFilterDefinitions}
            firstLabel={firstLabel}
            onUpdateFilters={onUpdateFilters}
          />
        </div>
      </div>

      <NodeAddRelationship
        node={node}
        editable={editable}
        onAddRelationship={onAddRelationship}
      />
    </div>
  );
};
